import { useTranslation } from "../../hooks/useTranslation";
import { useAdvancedState } from "../../hooks/use-advanced-state";
import React, { useCallback, useEffect, useState } from "react";
import { StringParam, useQueryParam, withDefault } from "use-query-params";
import { InputSelect } from "../../components/InputSelect";
import { StatisticsTops } from "./views/StatisticsTops";
import { StatisticsTracks } from "./views/StatisticsTracks";
import { StatisticsAlbums } from "./views/StatisticsAlbums";
import { Button } from "../../components/Button";
import { useMusicApi } from "../../api/useMusicApi";
import { SimpleViewModal } from "../../components/Modals/SimpleViewModal";
import { useLoading } from "../../hooks/use-loading";
import { Input } from "../../components/Input";
import { Preloader } from "../../components/Preloader";
import { saveAs } from "file-saver";
import { faDownload } from "@fortawesome/free-solid-svg-icons/faDownload";
import cn from "classnames";
import { useAuth } from "../../contexts/AuthContext";

export enum StatisticsType {
  TOPS = "TOPS",
  TRACKS = "TRACKS",
  ALBUMS = "ALBUMS",
}

export enum StatisticPlatform {
  VK_BOOM = "vk_boom",
  OK_BOOM = "ok_boom",
}

export enum StatisticPeriod {
  LAST_WEEK = "last_week",
  LAST_MONTH = "last_month",
}

let DOWNLOAD_TIMEOUT: NodeJS.Timeout | number | null = null;
export const StatisticsPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const {
    startDownloadStatisticsReport,
    startDownloadStatisticsReportWithoutDate,
    getCanAccessStatisticsReport,
    checkPrepareDownloadReport,
    downloadReport
  } = useMusicApi();
  const { loadings, startLoading, stopLoading } = useLoading({
    report: false
  });

  const [reportDate, setReportDate] = useState<Date | null>(null);
  const [canDownloadStreamsReport, setCanDownloadStreamsReport] = useState<boolean>(false);
  const [showDownloadDailyReportModal, setShowDownloadDailyReportModal] = useState<boolean>(false);
  const [showDownloadStreamsReportModal, setShowDownloadStreamsReportModal] = useState<boolean>(false);
  const [type, setType] = useQueryParam("content_type", withDefault(StringParam, StatisticsType.TOPS));
  const [counts, , updateCount] = useAdvancedState<{
    totalCount: string | number | null;
    contentCount: string | null | number;
  }>({
    totalCount: null,
    contentCount: null
  });

  useEffect(() => {
    getCanAccessStatisticsReport().then((res) => {
      setCanDownloadStreamsReport(res && !user?.has_stat_access);
    });
  }, []);

  const startDownloadReport = useCallback(
    async (type: "daily" | "streams") => {
      if (type === "daily" && reportDate == null) {
        return;
      }

      try {
        startLoading("report");
        let res = null;
        if (type === "daily") {
          res = await startDownloadStatisticsReport(reportDate as any);
        } else {
          res = await startDownloadStatisticsReportWithoutDate();
        }
        await checkReportDownloadStatus(res);
      } catch (e) {
        stopLoading("report");
      }
    },
    [reportDate]
  );

  const checkReportDownloadStatus = useCallback(async (taskId: string) => {
    try {
      let data = await checkPrepareDownloadReport(taskId);
      if (data !== "In progress") {
        let response = await downloadReport(data);
        const fileName = data.split("/")[1];
        saveAs(response, fileName);
        if (DOWNLOAD_TIMEOUT != null) {
          clearTimeout(DOWNLOAD_TIMEOUT as any);
        }
        stopLoading("report");
        setShowDownloadStreamsReportModal(false);
        return;
      }
      DOWNLOAD_TIMEOUT = setTimeout(
        () => checkReportDownloadStatus(taskId),
        5000
      );
    } catch (e) {
    }
  }, []);

  return (
    <>
      <div className={"w-100"}>
        <div className={"mb-3"}>
          <span className={"font-bold text-2xl dark:text-white"}>
            {t("sidebar_STATISTICS")}
          </span>{" "}
          {counts.totalCount != null && counts.contentCount != null && (
            <span className={"text-gray-500 font-bold dark:text-white"}>
              ({counts.contentCount} / {counts.totalCount})
            </span>
          )}
        </div>
      </div>
      <div className={"flex align-items-center justify-between"}>
        <div className={"my-3"} style={{ maxWidth: 200 }}>
          <InputSelect
            value={type}
            options={[
              {
                label: t("tops"),
                value: StatisticsType.TOPS
              },
              {
                label: t("tracks"),
                value: StatisticsType.TRACKS
              },
              {
                label: t("albums"),
                value: StatisticsType.ALBUMS
              }
            ]}
            onChange={(v) => setType(v.value as any)}
          />
        </div>
        <div>
          {user?.has_stat_access && (
            <Button
              label={t("download_statistics_daily_report").toString()}
              icon={faDownload}
              onClick={() => setShowDownloadDailyReportModal(true)}
            />
          )}
          {canDownloadStreamsReport && (
            <Button
              label={t("download_statistics_streams_report").toString()}
              className={"ml-2"}
              icon={faDownload}
              onClick={() => {
                startDownloadReport("streams").then(() =>
                  setShowDownloadStreamsReportModal(true)
                );
              }}
            />
          )}
        </div>
      </div>
      {type === StatisticsType.TOPS && (
        <StatisticsTops
          onChange={(totalCount, contentCount) =>
            updateCount({ totalCount, contentCount })
          }
        />
      )}
      {type === StatisticsType.ALBUMS && (
        <StatisticsAlbums
          onChange={(totalCount, contentCount) =>
            updateCount({ totalCount, contentCount })
          }
        />
      )}
      {type === StatisticsType.TRACKS && (
        <StatisticsTracks
          onChange={(totalCount, contentCount) =>
            updateCount({ totalCount, contentCount })
          }
        />
      )}

      {showDownloadDailyReportModal && (
        <SimpleViewModal
          title={t("download_stats_daily_report_title")}
          disableSuccess={loadings.report}
          className={cn("w-4/12")}
          onCancel={() => {
            if (DOWNLOAD_TIMEOUT != null) {
              clearTimeout(DOWNLOAD_TIMEOUT as any);
            }
            setShowDownloadDailyReportModal(false);
          }}
          onSuccess={() => startDownloadReport("daily")}
        >
          {loadings.report ? (
            <Preloader size={"2x"} className={"my-2"} />
          ) : (
            <Input
              label={t("choose_report_date").toString()}
              type={"date"}
              onChange={(value) => setReportDate(new Date(value))}
            />
          )}
        </SimpleViewModal>
      )}

      {showDownloadStreamsReportModal && (
        <SimpleViewModal
          title={t("download_stats_streams_report_title")}
          disableSuccess={loadings.report}
          className={cn("w-4/12")}
          onCancel={() => {
            if (DOWNLOAD_TIMEOUT != null) {
              clearTimeout(DOWNLOAD_TIMEOUT as any);
            }
            setShowDownloadStreamsReportModal(false);
          }}
        >
          <Preloader size={"2x"} className={"my-2"} />
        </SimpleViewModal>
      )}
    </>
  );
};
